import { Model } from '@/clients/http'

import type {
  BlogApiError,
  BlogByIdParams,
  BlogContentResponse,
  BlogCreateParams,
  BlogItemResponse,
  BlogListParams,
  BlogListResponse,
  BlogUpdateParams,
} from '@/types/blog'

export class BlogModel extends Model {
  public static readonly correlations = {
    GET_LIST: 'GET_BLOG_LIST',
    GET_BY_ID: 'GET_BLOG_BY_ID',
    GET_CONTENT: 'GET_BLOG_CONTENT',
    CREATE: 'CREATE_BLOG',
    UPDATE: 'UPDATE_BLOG',
    DELETE: 'DELETE_BLOG',
    RESTORE: 'RESTORE_BLOG',
    PERMANENT_DELETE: 'PERMANENT_DELETE_BLOG',
  }

  public static readonly endpoints = {
    list: (props?: BlogListParams) =>
      this.createEndpoint({ url: '/api/backoffice/blog', query: props?.query }),
    byId: (props: BlogByIdParams) =>
      this.createEndpoint({ url: '/api/backoffice/blog/:id', params: props.params, query: props.query }),
    content: (props: BlogByIdParams) => this.createEndpoint({ url: '/api/backoffice/blog/:id/content', params: props.params }),
    restore: (props: BlogByIdParams) => this.createEndpoint({ url: '/api/backoffice/blog/:id/restore', params: props.params }),
    permanent: (props: BlogByIdParams) => this.createEndpoint({ url: '/api/backoffice/blog/permanent/:id', params: props.params }),
  }

  public static readonly getList = async (props?: BlogListParams) =>
    this.createHttpRequest<BlogListResponse, BlogApiError>({
      correlationKey: this.correlations.GET_LIST,
      payload: { method: 'get', url: this.endpoints.list(props), useBearerToken: true },
    })

  public static readonly getById = async (props: BlogByIdParams) =>
    this.createHttpRequest<BlogItemResponse, BlogApiError>({
      correlationKey: this.correlations.GET_BY_ID,
      payload: { method: 'get', url: this.endpoints.byId(props), useBearerToken: true },
    })

  public static readonly getContent = async (props: BlogByIdParams) =>
    this.createHttpRequest<BlogContentResponse, BlogApiError>({
      correlationKey: this.correlations.GET_CONTENT,
      payload: { method: 'get', url: this.endpoints.content(props), useBearerToken: true },
    })

  public static readonly create = async (props: BlogCreateParams) =>
    this.createHttpRequest<BlogItemResponse, BlogApiError>({
      correlationKey: this.correlations.CREATE,
      payload: { method: 'post', url: this.endpoints.list(), body: props.body, useBearerToken: true },
    })

  public static readonly update = async (props: BlogUpdateParams) =>
    this.createHttpRequest<BlogItemResponse, BlogApiError>({
      correlationKey: this.correlations.UPDATE,
      payload: { method: 'put', url: this.endpoints.byId(props), body: props.body, useBearerToken: true },
    })

  public static readonly delete = async (props: BlogByIdParams) =>
    this.createHttpRequest<{ message: string }, BlogApiError>({
      correlationKey: this.correlations.DELETE,
      payload: { method: 'delete', url: this.endpoints.byId(props), useBearerToken: true },
    })

  public static readonly restore = async (props: BlogByIdParams) =>
    this.createHttpRequest<{ message: string }, BlogApiError>({
      correlationKey: this.correlations.RESTORE,
      payload: { method: 'post', url: this.endpoints.restore(props), useBearerToken: true },
    })

  /** Deletes a trashed post for good; the result carries the body file's media id. */
  public static readonly permanentDelete = async (props: BlogByIdParams) =>
    this.createHttpRequest<{ message: string; result?: { content_id?: string | null } }, BlogApiError>({
      correlationKey: this.correlations.PERMANENT_DELETE,
      payload: { method: 'delete', url: this.endpoints.permanent(props), useBearerToken: true },
    })
}
